import { useMutation, useQueryClient } from "react-query";
import { TOpenModalOperadoraCartao } from ".."
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { IEstabelecimento, TOperadoraCartao } from "../../../../../../types/api/financeiro/operadoras-cartao";
import { Dialog, TextField, DialogContent, DialogTitle, Grid, DialogActions, Button, CircularProgress } from "@mui/material";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import SelecaoEstabelecimentos from "./SelecaoEstabelecimentos";
import SelectFormaRecebimento, { TObjValueFormRecebimento } from "../../../../../../components/SelectsSimpleApiQuery/SelectFormaRecebimento";
import SelectLoja from "../../../../../../components/SelectsSimpleApiQuery/SelectLoja";
import SelectPublico from "../../../../../../components/SelectsSimpleApiQuery/SelectPublico";

export default function DialogCriacaoEditOperadorasCartao({
    defaultValue,
    open,
    handleClose
}: DialogCriacaoEditOperadorasCartaoProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])
    //

    const methods = useForm<TOperadoraCartao>({
        defaultValues: {
            id: valoresIniciais?.id,
            nome: valoresIniciais?.nome,
            cooperativa_id: valoresIniciais?.cooperativa_id,
            estabelecimentos: valoresIniciais?.estabelecimentos,
            loja: valoresIniciais?.loja,
            forma_recebimento: valoresIniciais?.forma_recebimento || null,
            publico: valoresIniciais?.publico,
            maximo_parcelas: valoresIniciais?.maximo_parcelas
        }
    })
    const { control, watch, handleSubmit, getValues, setValue, getFieldState } = methods;

    //Querys e mutations
    const { mutate: criarOperadoraCartao, isLoading: isLoadingCriarOperadoraCartao } = useMutation({
        mutationKey: 'cadastrar-operadora-cartao',
        mutationFn: (inputs: TParams) => axiosClient.post("financeiro/configuracoes/cadastrar-operadora-cartao", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar operadora de cartão!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-operadoras-cartao"])
            handleClose()
            enqueueSnackbar('Cadastro de operadora de cartão realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarOperadoraCartao, isLoading: isLoadingAttOperadoraCartao } = useMutation({
        mutationKey: 'atualizar-operadora-cartao',
        mutationFn: (inputs: TParams) => axiosClient.put(`financeiro/configuracoes/atualizar-operadora-cartao/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar operadora de cartão!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-operadoras-cartao"])
            handleClose()
            enqueueSnackbar('Atualização de operadora de cartão com sucesso!', { variant: 'success' });
        }
    })
    //  

    //Funções manipuladoras
    const handleAddEstabelecimentos = (valor: IEstabelecimento) => {
        const estabelecimentos = getValues('estabelecimentos') || []
        const index = estabelecimentos.findIndex((_, indexItem) => indexItem === valor.id);

        if (index !== -1) {
            const newForm = [...estabelecimentos];
            newForm[index] = valor;
            setValue('estabelecimentos', newForm);
        }

        if (index === -1) setValue('estabelecimentos', [...estabelecimentos, valor]);
    }

    const handleDeleteEstabelecimentos = (valor: IEstabelecimento) => {
        let estabelecimentos = getValues('estabelecimentos') || []
        estabelecimentos = estabelecimentos.filter((item: IEstabelecimento) => item.id !== valor.id)
        setValue('estabelecimentos', estabelecimentos)
    }

    const onSubmit = (data: TOperadoraCartao) => {
        if (!data.loja || !data.forma_recebimento) return

        const params: TParams = {
            nome: data.nome,
            loja_id: data.loja.id,
            forma_recebimento_id: data.forma_recebimento.id,
            estabelecimentos: data.estabelecimentos.map((item: IEstabelecimento) => {
                return {
                    loja_id: item.loja.id,
                    numero_estabelecimento: item.numero_estabelecimento
                }
            }),
            tipo_publico: data.publico.tipo_publico,
            codigo_publico: Number(data.publico.codigo_publico),
            maximo_parcelas: Number(data.maximo_parcelas)
        }
        if (isEdit) atualizarOperadoraCartao(params)
        if (!isEdit) criarOperadoraCartao(params)
    }

    const nome = watch('nome');
    const estabelecimentos = watch('estabelecimentos')
    const loja = watch('loja')
    const formaRecebimento = watch('forma_recebimento')

    const podeContinuar = useMemo(() => {
        const estabelecimentosArray = estabelecimentos || [];
        if (
            estabelecimentosArray.length === 0
            || nome === ''
            || loja === null
            || formaRecebimento === null
        ) return false
        return true
    }, [nome, estabelecimentos, loja, formaRecebimento])
    //

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {`${!isEdit ? "Adicionar" : "Editar"} operadora de cartão`}
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} mt={1}>

                    <Grid size={{ xs: 12 }}>
                        <Controller
                            name="nome"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    defaultValue={valoresIniciais?.nome}
                                    label="Nome"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    error={getFieldState('nome').invalid}
                                    helperText={getFieldState('nome').error?.message || ""}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name="publico"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => {
                                return (
                                    <SelectPublico
                                        {...field}
                                        error={invalid}
                                        required
                                        errorMessage={error?.message}
                                        value={field.value}
                                        onChange={(valor) => field.onChange(valor)}
                                    />
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                            name="loja"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field }) => (
                                <SelectLoja
                                    {...field}
                                    value={field.value || null}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        setValue('forma_recebimento', null)
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {
                        loja &&
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="forma_recebimento"
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field }) => (
                                    <SelectFormaRecebimento
                                        {...field}
                                        loja_id={watch('loja')?.id || 0}
                                        value={field.value as TObjValueFormRecebimento}
                                        onChange={(value) => setValue('forma_recebimento', value)}
                                    />
                                )}
                            />
                        </Grid>
                    }

                    <Grid size={{ xs: 6 }}>
                        <Controller
                            name="maximo_parcelas"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="number"
                                    label="Máximo de parcelas"
                                    required
                                    variant="outlined"
                                    fullWidth
                                    error={getFieldState('maximo_parcelas').invalid}
                                    helperText={getFieldState('maximo_parcelas').error?.message || ""}
                                    slotProps={{ htmlInput: { maxLength: 1, min: 1, max: 9, step: 1 } }}
                                    onKeyDown={e => {
                                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    onInput={e => {
                                        const input = e.target as HTMLInputElement;
                                        if (input.value.length > 1) {
                                            input.value = input.value.slice(0, 1);
                                        }
                                        field.onChange(input.value);
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <SelecaoEstabelecimentos
                            estabelecimentos={watch('estabelecimentos') || []}
                            onSubmit={(valor: IEstabelecimento) => handleAddEstabelecimentos(valor)}
                            onDelete={(valor: IEstabelecimento) => handleDeleteEstabelecimentos(valor)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    autoFocus
                    disabled={!podeContinuar || isLoadingCriarOperadoraCartao || isLoadingAttOperadoraCartao}
                    onClick={handleSubmit(onSubmit)}
                >
                    {
                        (isLoadingCriarOperadoraCartao || isLoadingAttOperadoraCartao) &&
                        <CircularProgress size={20} sx={{ marginRight: 1 }} />
                    }
                    {!isEdit ? "Adicionar" : "Atualizar"}
                </Button>
            </DialogActions>

        </Dialog>
    )
}

interface DialogCriacaoEditOperadorasCartaoProps {
    defaultValue: TOpenModalOperadoraCartao,
    open: boolean,
    handleClose: () => void
}

type TParams = {
    nome: string,
    loja_id: number,
    forma_recebimento_id: number,
    estabelecimentos: Array<{
        loja_id: number,
        numero_estabelecimento: string
    }>,
    tipo_publico: string,
    codigo_publico: number
    maximo_parcelas: number
}
